import React, { useContext, useState, useEffect, useRef, useCallback } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import InputHelpText from 'shared/forms/input-help-text'
import UserContext from 'context/user-context'
import useMunicipalitySettings, { useUpdateMunicipalitySettings } from 'api/hooks/use-municipality-settings'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

const SEGMENT_FIELDS = [
  { key: 'seg_one_name', label: 'Segment One' },
  { key: 'seg_two_name', label: 'Segment Two' },
  { key: 'seg_three_name', label: 'Segment Three' },
  { key: 'seg_four_name', label: 'Segment Four' },
  { key: 'seg_five_name', label: 'Segment Five' },
  { key: 'seg_six_name', label: 'Segment Six' },
  { key: 'seg_seven_name', label: 'Segment Seven' },
  { key: 'seg_eight_name', label: 'Segment Eight' }
]

function GeneralSettings() {
  const { municipality } = useContext(UserContext)
  const queryClient = useQueryClient()
  const { data: settings } = useMunicipalitySettings({ options: {} })

  const [yearsOfData, setYearsOfData] = useState('')
  const [segments, setSegments] = useState({})
  const debounceTimerRef = useRef(null)

  useEffect(() => {
    if (settings) {
      setYearsOfData(settings.years_of_data ?? '')
      setSegments({
        seg_one_name: settings.seg_one_name || '',
        seg_two_name: settings.seg_two_name || '',
        seg_three_name: settings.seg_three_name || '',
        seg_four_name: settings.seg_four_name || '',
        seg_five_name: settings.seg_five_name || '',
        seg_six_name: settings.seg_six_name || '',
        seg_seven_name: settings.seg_seven_name || '',
        seg_eight_name: settings.seg_eight_name || ''
      })
    }
  }, [settings])

  const updateMutation = useUpdateMunicipalitySettings({
    options: {
      onSuccess: () => {
        toast.success('Settings saved successfully')
        queryClient.invalidateQueries('municipality-settings')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to save settings')
      }
    }
  })

  const handleYearsOfDataChange = (value) => {
    setYearsOfData(value)
    updateMutation.mutate({ years_of_data: value })
  }

  const debouncedSave = useCallback((updatedSegments) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      updateMutation.mutate(updatedSegments)
    }, 500)
  }, [updateMutation])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const handleSegmentChange = (key, value) => {
    const updatedSegments = { ...segments, [key]: value }
    setSegments(updatedSegments)
    debouncedSave(updatedSegments)
  }

  const {
    fiscal_year_start_month,
    fiscal_year_start_day
  } = municipality.setting

  function getMonthAbbreviation(monthNumber) {
    const monthAbbreviations = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ]
    return monthAbbreviations[monthNumber - 1]
  }

  return (
    <Row>
      <Col>
        <Card>
          <Card.Body>
            <Row>
              <Col>
                <div className="text-primary fw-bold mb-2 fs-5">
                  Municipality
                  <hr />
                </div>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col sm={8}>
                <p className="mt-1">Name</p>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    disabled
                    value={municipality.name}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col sm={8}>
                <p className="mt-1">Abbreviation</p>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    disabled
                    value={municipality.abbreviation}
                    maxLength={3}
                  />
                  <InputHelpText content="Used for testing attribute abbreviations" />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col sm={8}>
                <p className="mt-1">Fiscal Year Start Date</p>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    disabled
                    value={`${getMonthAbbreviation(
                      fiscal_year_start_month
                    )}. ${fiscal_year_start_day}`}
                  />
                </Form.Group>
              </Col>
            </Row>


<Row className="mt-4">
              <Col>
                <div className="text-primary fw-bold mb-2 fs-5">
                  Analytics
                  <hr />
                </div>
              </Col>
            </Row>

            <Row className="mt-3">
                <Col sm={8}>
                  <p className="mt-1">Years of Data</p>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Select
                      value={yearsOfData}
                      onChange={(e) => handleYearsOfDataChange(e.target.value)}
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="all">All</option>
                    </Form.Select>
                    <InputHelpText content="The number of years of data to update nightly. Higher number of years may lead to decreased performance." />
                  </Form.Group>
                </Col>
              </Row>

            <Row className="mt-4">
              <Col>
                <div className="text-primary fw-bold mb-2 fs-5">
                  Accounts
                  <hr />
                </div>
              </Col>
            </Row>

            {SEGMENT_FIELDS.map(({ key, label }) => (
              <Row className="mt-3" key={key}>
                <Col sm={8}>
                  <p className="mt-1">{label}</p>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Control
                      type="text"
                      value={segments[key] || ''}
                      onChange={(e) => handleSegmentChange(key, e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            ))}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default GeneralSettings
